import * as VisuallyHiddenPrimitive from '@radix-ui/react-visually-hidden';
import {
  BeneficiaryType,
  EvidenceStatus,
  FinancialServiceProviderAdapters,
  LegalEntityCountry,
  PaymentDestinationType,
  VerifyPayeeCheckResponseCode,
} from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  AuthorisationResponseType,
  CreateBeneficiaryRequestType,
  CreatePaymentRequestType,
  PaymentType,
  UpdateBeneficiaryRequestType,
} from 'PublicEntityTypes';
import { ComponentProps, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { usePayeeCheck } from '../../../api/use-payee-check';
import { BENEFICIARY_API_PATH, EVIDENCE_API_PATH } from '../../../contexts/data-context/data-provider';
import { useData } from '../../../contexts/data-context/use-data';
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalDescription,
  ModalTitle,
} from '../../../design-system/modal/modal';
import { AccountView, BeneficiaryView, InvoiceEvidenceView } from '../../../types/entity-types';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getSwrFetcher } from '../../../utils/get-swr-fetcher';
import { isPayeeCheckWarningRequired } from '../../../utils/payee-check/is-payee-check-warning-required';
import { isVopCheckRequired } from '../../../utils/payee-check/is-vop-check-required';
import { isVopPayeeCheck } from '../../../utils/payee-check/is-vop-payee-check';
import { postSwrFetcher } from '../../../utils/post-swr-fetcher';
import { postSwrFetcherWithFile } from '../../../utils/post-swr-fetcher-with-file';
import { putSwrFetcherWithDynamicUrl } from '../../../utils/put-swr-fetcher-with-dynamic-url';
import { useXeroConnection } from '../../invoice-pay/utils/use-xero-connection';
import { BeneficiaryListFlow } from '../beneficiary-list-flow/beneficiary-list-flow';
import { BillPayFlow, BillPayFlowStep } from '../bill-pay-flow/bill-pay-flow';
import { BillPayUploadInvoiceFlow } from '../bill-pay-upload-invoice-flow/bill-pay-upload-invoice-flow';
import {
  CreateBeneficiaryFlow,
  CreateBeneficiaryFlowFormValues,
} from '../create-beneficiary-flow/create-beneficiary-flow';
import { ExternalPaymentForm } from '../external-payment-flow/components/external-payment-form';
import { ExternalPaymentFlow, ExternalPaymentFlowStep } from '../external-payment-flow/external-payment-flow';
import { InternalPaymentForm } from '../internal-payment-flow/components/internal-payment-form';
import { InternalPaymentFlow, InternalPaymentFlowStep } from '../internal-payment-flow/internal-payment-flow';
import { PaymentDetails } from '../payment-flow-storage';
import { PaymentAction, usePaymentFlow } from '../use-payment-flow';
import { getAccountDetails } from '../utils/get-account-details';
import { getBeneficiaryName } from '../utils/get-beneficiary-name';
import { getEntryPointPath } from '../utils/get-entry-point-path';
import { getPaymentDestinationType } from '../utils/get-payment-destination-type';
import { getXeroCardConfig } from '../utils/get-xero-card-config';
import { isSupportedCurrency } from '../utils/is-supported-currency';
import { PayeeCheckConfirmationModal } from './payee-check-confirmation-modal';
import { PayeeCheckModal } from './payee-check-modal';
import { PaymentsFlowContentWrapper } from './payments-flow-content-wrapper';

const POLLING_INTERVAL = 1000;

const PAYMENTS_API_PATH = '/api/payments';
const OPEN_BANKING_PAYMENTS_API_PATH = '/api/open-banking/payments/authorise';
const EVIDENCE_BASE_API_PATH = '/api/evidence';
const UPLOAD_INVOICE_API_PATH = `${EVIDENCE_BASE_API_PATH}/invoice`;

export const IS_XERO_CARD_HIDDEN_KEY = 'is_xero_card_hidden';

type PaymentResponse = PaymentType | AuthorisationResponseType;

type CompanyData = {
  id: string;
  country: LegalEntityCountry;
};

type UploadInvoiceRequest = { companyId: string; file: File };

type UploadInvoiceResponse = {
  evidence: InvoiceEvidenceView;
  document: DocumentData;
};

type DocumentData = { id: string; fileName: string; url: string };

export type PayeeModalVariantConfig =
  | { variant: VerifyPayeeCheckResponseCode.CLOSE_MATCH; name: string }
  | {
      variant: Exclude<
        VerifyPayeeCheckResponseCode,
        VerifyPayeeCheckResponseCode.MATCHED | VerifyPayeeCheckResponseCode.CLOSE_MATCH
      >;
    };

type PaymentsFlowModalProps = {
  isOpen: boolean;
  onClose: () => void;
  paymentAction: PaymentAction;
  beneficiaries: BeneficiaryView[];
  selectedBeneficiaryId: string | null;
  accounts: AccountView[];
  companyData: CompanyData;
  evidences: InvoiceEvidenceView[];
  onSetPaymentAction: (action: PaymentAction) => void;
  onSelectBeneficiary: (beneficiaryId: string) => void;
  className?: string;
};

export const PaymentsFlowModal = ({
  isOpen,
  onClose,
  paymentAction,
  beneficiaries,
  selectedBeneficiaryId,
  accounts,
  companyData,
  evidences,
  onSetPaymentAction,
  onSelectBeneficiary,
  className,
}: PaymentsFlowModalProps) => {
  const { getPaymentsFlowEntryPoint } = usePaymentFlow();
  const navigate = useNavigate();

  const paymentsFlowEntryPoint = getPaymentsFlowEntryPoint();

  const onOpenChange = () => {
    const entryPointPath =
      paymentsFlowEntryPoint === 'INVOICE_PAGE' ||
      paymentsFlowEntryPoint === 'TREASURY_PAGE_DEPOSIT' ||
      paymentsFlowEntryPoint === 'TREASURY_PAGE_WITHDRAWAL'
        ? getEntryPointPath(paymentsFlowEntryPoint)
        : null;

    onClose();

    if (entryPointPath) {
      navigate(entryPointPath);
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange}>
      <ModalContent className={className}>
        <VisuallyHiddenPrimitive.Root asChild>
          <div>
            <ModalTitle>Select category</ModalTitle>

            <ModalDescription>Modal to change the category assigned to a sub-category</ModalDescription>
          </div>
        </VisuallyHiddenPrimitive.Root>
        <ModalCloseButton onClick={onOpenChange} />

        <PaymentsFlowContent
          paymentAction={paymentAction}
          onGoBack={onOpenChange}
          beneficiaries={beneficiaries}
          selectedBeneficiaryId={selectedBeneficiaryId}
          accounts={accounts}
          companyData={companyData}
          evidences={evidences}
          onSetPaymentAction={onSetPaymentAction}
          onSelectBeneficiary={onSelectBeneficiary}
        />
      </ModalContent>
    </Modal>
  );
};

type SubmitPaymentArgs = Parameters<ComponentProps<typeof InternalPaymentForm>['onSubmit']>[0];

type PaymentsFlowContentProps = {
  paymentAction: PaymentAction;
  beneficiaries: BeneficiaryView[];
  selectedBeneficiaryId: string | null;
  accounts: AccountView[];
  companyData: CompanyData;
  evidences: InvoiceEvidenceView[];
  onGoBack: () => void;
  onSetPaymentAction: ComponentProps<typeof PaymentsFlowModal>['onSetPaymentAction'];
  onSelectBeneficiary: ComponentProps<typeof PaymentsFlowModal>['onSelectBeneficiary'];
};

const PaymentsFlowContent = ({
  paymentAction,
  beneficiaries,
  selectedBeneficiaryId,
  accounts,
  companyData,
  evidences,
  onGoBack,
  onSetPaymentAction,
  onSelectBeneficiary,
}: PaymentsFlowContentProps) => {
  const { isLongXeroFlowLive, isVopPaymentsLive } = useFlags<{
    isLongXeroFlowLive: boolean;
    isVopPaymentsLive: boolean;
  }>();
  const { mutations } = useData();
  const {
    updateShouldRenderReviewStep,
    clearShouldRenderReviewStep,
    updatePaymentDetails,
    updateDocumentDetails,
    uploadedEvidenceId,
    updateUploadedEvidenceId,
    clearBillPayState,
    getUploadedEvidenceId,
    updateEvidencePrefill,
    getPaymentsFlowEntryPoint,
    updatePaymentsFlowEntryPoint,
    shouldSkipLoadingStep,
    formValuesData,
    getPaymentDetails,
  } = usePaymentFlow();
  const {
    mutations: { triggerVerifyPayee, triggerUpdateBeneficiary },
    isMutating: { isVerifyingPayee },
  } = usePayeeCheck();

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isEditingBeneficiary, setIsEditingBeneficiary] = useState(false);
  const [internalPaymentStep, setInternalPaymentStep] = useState<InternalPaymentFlowStep>('PAYMENT_FORM');
  const [externalPaymentStep, setExternalPaymentStep] = useState<ExternalPaymentFlowStep>('PAYMENT_FORM');
  const [billPayFlowStep, setBillPayFlowStep] = useState<BillPayFlowStep>('FLOW');

  const { xeroConnectionStatus, isCheckingXeroConnection } = useXeroConnection();
  const [isXeroCardHidden, setIsXeroCardHidden] = useState(
    () => localStorage.getItem(IS_XERO_CARD_HIDDEN_KEY) === 'true',
  );
  const shouldDisplayXeroCard =
    !isXeroCardHidden && isLongXeroFlowLive && !isCheckingXeroConnection && !xeroConnectionStatus?.connected;
  const [payeeCheckModalVariantConfig, setPayeeCheckModalVariantConfig] = useState<PayeeModalVariantConfig | undefined>(
    undefined,
  );
  const [payeeConfirmationModalVariantConfig, setPayeeConfirmationModalVariantConfig] = useState<
    PayeeModalVariantConfig | undefined
  >(undefined);

  const { trigger: triggerPayment } = useSWRMutation<PaymentResponse, Error, string, CreatePaymentRequestType>(
    PAYMENTS_API_PATH,
    postSwrFetcher,
  );
  const { trigger: createBeneficiary } = useSWRMutation<BeneficiaryView, Error, string, CreateBeneficiaryRequestType>(
    BENEFICIARY_API_PATH,
    postSwrFetcher,
  );
  const { trigger: triggerOpenBankingPayment } = useSWRMutation<
    PaymentResponse,
    Error,
    string,
    CreatePaymentRequestType
  >(OPEN_BANKING_PAYMENTS_API_PATH, postSwrFetcher);

  // TODO: Refactor to use a single triggerPayment with dynamic URL
  // Currently we have two separate triggers that only differ by URL
  // Consider using SWR's array keys and fetcher logic to make the URL dynamic
  // See: https://swr.vercel.app/docs/arguments#multiple-arguments
  const triggerPaymentBasedOnAdapter = async (body: CreatePaymentRequestType, sourceAccount?: AccountView) => {
    if (sourceAccount?.fspAdapter === FinancialServiceProviderAdapters.YAPILY) {
      return await triggerOpenBankingPayment(body);
    }
    // Add Modulr logic here if needed
    return await triggerPayment(body);
  };
  const { trigger: triggerUploadInvoice, isMutating: isUploadingInvoice } = useSWRMutation<
    UploadInvoiceResponse,
    Error,
    string,
    UploadInvoiceRequest
  >(UPLOAD_INVOICE_API_PATH, postSwrFetcherWithFile);
  const { trigger: triggerEvidenceUpdate } = useSWRMutation<
    InvoiceEvidenceView,
    Error,
    string,
    { suggestedBeneficiaryId?: string | null } & { urlSuffix: string }
  >(EVIDENCE_API_PATH, putSwrFetcherWithDynamicUrl);

  const { data: evidenceData, error: evidenceDataError } = useSWR<InvoiceEvidenceView, Error, string | null>(
    uploadedEvidenceId && !shouldSkipLoadingStep ? `${EVIDENCE_BASE_API_PATH}/${uploadedEvidenceId}` : null,
    getSwrFetcher,
    {
      refreshInterval: (data) => {
        if (!data) {
          return POLLING_INTERVAL;
        }

        const isEvidenceFinalised =
          data.status === EvidenceStatus.IS_PAID_PROCESSED ||
          data.status === EvidenceStatus.PROCESSED ||
          data.status === EvidenceStatus.FAILED;

        return isEvidenceFinalised ? 0 : POLLING_INTERVAL;
      },
      refreshWhenHidden: true,
      onSuccess: async (data) => {
        if (!uploadedEvidenceId) {
          // Store evidence ID to persist across SCA (Strong Customer Authentication) flow
          // This ensures we can retrieve the evidence details when the user returns from authentication
          updateUploadedEvidenceId(data.id);
        }

        if (data.status === EvidenceStatus.PROCESSED || data.status === EvidenceStatus.FAILED) {
          await mutations.handleIncrementalUpdates();
          updateEvidencePrefill({
            beneficiaryId: data.suggestedBeneficiaryId,
            reference: data.invoiceDetails?.invoiceReference,
          });
        }
      },
    },
  );
  const navigate = useNavigate();

  const handlePaymentSuccess = async () => {
    await mutations.handleIncrementalUpdates();

    toast.success('Payment created successfully');

    // TODO: should we clear payment details here w clearStoredPaymentDetails?
    // I think so but I'd rather examine it in a separate PR not to break existing flows
  };

  const onUploadInvoice = async (files: File[]) => {
    try {
      const response = await triggerUploadInvoice({
        companyId: companyData.id,
        file: files[0],
      });

      onSetPaymentAction('BILL_PAY');

      updateUploadedEvidenceId(response.evidence.id);
      updateDocumentDetails({
        fileName: response.document.fileName,
        documentUrl: response.document.url,
      });
    } catch (error) {
      Sentry.addBreadcrumb({
        data: {
          companyData,
          files,
          error,
          message: 'triggerUploadInvoice failed',
        },
      });

      Sentry.captureException(error);

      toast.error('Failed to upload invoice');
    }
  };

  const onConnectXero = () => {
    navigate('/integrations');
  };

  const onHideXeroCard = () => {
    localStorage.setItem(IS_XERO_CARD_HIDDEN_KEY, 'true');
    setIsXeroCardHidden(true);
  };

  const createXeroCardConfig = (
    flowStep: InternalPaymentFlowStep | ExternalPaymentFlowStep | BillPayFlowStep,
  ): NonNullable<ComponentProps<typeof PaymentsFlowContentWrapper>['xeroCardConfig']> => {
    const baseConfig = getXeroCardConfig({
      flowStep,
      shouldDisplayCard: shouldDisplayXeroCard,
    });
    return baseConfig.isCardVisible ? { ...baseConfig, onConnect: onConnectXero, onClose: onHideXeroCard } : baseConfig;
  };

  switch (paymentAction) {
    case 'INTERNAL_PAYMENT': {
      const handleSubmitInternalPayment = async ({
        values,
        currency,
        sourceAccountName,
        sourceAccountLogo,
        destinationAccountType,
        destinationAccountName,
        destinationAccountLogo,
        isInstantTransfer,
      }: SubmitPaymentArgs) => {
        setIsProcessingPayment(true);

        try {
          const body: Parameters<typeof triggerPayment>[0] = {
            amount: values.amount,
            currency,
            destination: {
              type: getPaymentDestinationType(destinationAccountType),
              id: values.destinationAccountId,
              name: destinationAccountName,
            },
            sourceAccountId: values.sourceAccountId,
          };

          const sourceAccount = accounts.find((a) => a.id === values.sourceAccountId);

          Sentry.addBreadcrumb({
            data: {
              body,
              sourceAccount,
              message: 'Internal payment request body',
            },
          });

          const response = await triggerPaymentBasedOnAdapter(body, sourceAccount);

          const paymentDetails = {
            sourceAccountId: values.sourceAccountId,
            sourceAccountName,
            sourceAccountLogo,
            destinationAccountName,
            destinationAccountLogo,
            amount: values.amount,
            currency,
            isInstantTransfer,
          };

          updatePaymentDetails(paymentDetails);

          // We need this to show the payment details in the success step
          // TODO: remove this once we can query the paymentId returned from the SCA flow
          if (isAuthorisationResponse(response)) {
            // Triggers the SCA flow
            window.location.href = response.authorisationUrl;

            return;
          }

          setInternalPaymentStep('SUCCESS');

          handlePaymentSuccess();
        } catch (error) {
          Sentry.captureException(error);

          toast.error('Failed to create payment');
        } finally {
          setIsProcessingPayment(false);
        }
      };

      const xeroCardConfig = createXeroCardConfig(internalPaymentStep);

      return (
        <PaymentsFlowContentWrapper xeroCardConfig={xeroCardConfig}>
          <InternalPaymentFlow
            accounts={accounts}
            isProcessingPayment={isProcessingPayment}
            paymentStep={internalPaymentStep}
            onSubmit={handleSubmitInternalPayment}
            onGoBack={() => {
              onGoBack();
            }}
            onSuccessfulAuthentication={() => {
              setInternalPaymentStep('SUCCESS');

              handlePaymentSuccess();
            }}
          />
        </PaymentsFlowContentWrapper>
      );
    }
    case 'EXTERNAL_PAYMENT': {
      const selectedBeneficiary = beneficiaries.find((b) => b.id === selectedBeneficiaryId);

      if (!selectedBeneficiary) {
        Sentry.captureMessage(
          'Selected beneficiary cannot be found among the list of beneficiaries in EXTERNAL_PAYMENT flow',
          {
            extra: {
              selectedBeneficiaryId,
              beneficiaries: beneficiaries.map((b) => b.id),
              paymentAction,
              message: 'Selected beneficiary cannot be found among the list of beneficiaries in EXTERNAL_PAYMENT flow',
            },
          },
        );

        return null;
      }

      const { type, accountNumber, sortCode, iban, currency } = selectedBeneficiary.destination;

      // TODO: to confirm if we support payments in all currencies
      if (!isSupportedCurrency(currency)) {
        Sentry.captureMessage('Unsupported currency when attempting payment in EXTERNAL_PAYMENT flow', {
          extra: {
            currency,
            selectedBeneficiaryId,
            selectedBeneficiaryDestination: selectedBeneficiary.destination,
            beneficiaries: beneficiaries.map((b) => b.id),
            paymentAction,
            message: 'Unsupported currency when attempting payment in EXTERNAL_PAYMENT flow',
          },
        });

        return null;
      }

      const beneficiaryAccountDetails = getAccountDetails({
        type,
        accountNumber,
        sortCode,
        iban,
      });

      const handleSubmitExternalPayment = async (paymentDetails: PaymentDetails) => {
        if (!selectedBeneficiaryId) {
          Sentry.captureMessage(
            'Selected beneficiary id cannot be retrieved when calling handleSubmitExternalPayment in EXTERNAL_PAYMENT flow',
            {
              extra: {
                selectedBeneficiaryId,
                beneficiaries: beneficiaries.map((b) => b.id),
                paymentAction,
                message:
                  'Selected beneficiary id cannot be retrieved when calling handleSubmitExternalPayment in EXTERNAL_PAYMENT flow',
              },
            },
          );

          return;
        }

        const { amount, currency, sourceAccountId, reference, destinationAccountName } = paymentDetails;

        setIsProcessingPayment(true);

        try {
          const body = {
            amount,
            currency,
            destination: {
              type: PaymentDestinationType.BENEFICIARY,
              id: selectedBeneficiaryId,
              name: destinationAccountName,
            },
            sourceAccountId,
            reference,
          };

          const sourceAccount = accounts.find((a) => a.id === sourceAccountId);

          Sentry.addBreadcrumb({
            data: {
              body,
              sourceAccount,
              message: 'External payment request body',
            },
          });

          const response = await triggerPaymentBasedOnAdapter(body, sourceAccount);

          if (isAuthorisationResponse(response)) {
            // Triggers the SCA flow
            window.location.href = response.authorisationUrl;

            return;
          }

          setExternalPaymentStep('SUCCESS');

          handlePaymentSuccess();
        } catch (error) {
          Sentry.captureException(error);

          toast.error('Failed to create payment');
        } finally {
          setIsProcessingPayment(false);
        }
      };

      const preparePaymentReview = (values: Parameters<ComponentProps<typeof ExternalPaymentForm>['onSubmit']>[0]) => {
        console.log('payment details sent for review');

        const paymentDetails = {
          sourceAccountId: values.sourceAccountId,
          sourceAccountName: accounts.find((a) => a.id === values.sourceAccountId)?.displayName ?? 'Bank Account',
          destinationAccountName: selectedBeneficiary.name,
          amount: values.amount,
          currency,
          reference: values.paymentReference,
        };

        updatePaymentDetails(paymentDetails);

        setExternalPaymentStep('REVIEW');
      };

      const handleVopCheckResult = (
        code: VerifyPayeeCheckResponseCode,
        matchedName: string = '',
        values: Parameters<typeof preparePaymentReview>[0],
      ) => {
        switch (code) {
          case VerifyPayeeCheckResponseCode.MATCHED:
            preparePaymentReview(values);

            return;
          case VerifyPayeeCheckResponseCode.CLOSE_MATCH:
            setPayeeCheckModalVariantConfig({ variant: code, name: matchedName });
            formValuesData.externalPayment.setValues(values);

            return;
          case VerifyPayeeCheckResponseCode.NO_MATCH:
          case VerifyPayeeCheckResponseCode.CHECK_NOT_POSSIBLE:
          case VerifyPayeeCheckResponseCode.INVALID:
          case VerifyPayeeCheckResponseCode.GENERAL:
          case VerifyPayeeCheckResponseCode.INTERNAL_ERROR:
            setPayeeCheckModalVariantConfig({ variant: code });
            formValuesData.externalPayment.setValues(values);

            return;
          default:
            return exhaustiveCheck(code);
        }
      };

      const onContinuePaymentForm = async (
        values: Parameters<ComponentProps<typeof ExternalPaymentForm>['onSubmit']>[0],
      ) => {
        const sourceAccount = accounts.find((a) => a.id === values.sourceAccountId);

        if (!sourceAccount?.fspAdapter) {
          Sentry.captureMessage(
            'Source account fspAdapter cannot be retrieved when calling onContinuePaymentForm in EXTERNAL_PAYMENT flow',
            {
              extra: {
                values,
                accountIds: accounts.map((a) => a.id),
              },
            },
          );

          return;
        }

        const shouldTriggerVopCheck =
          isVopCheckRequired({
            currency: selectedBeneficiary.destination.currency,
            fspAdapter: sourceAccount.fspAdapter,
          }) && isVopPaymentsLive;

        if (!shouldTriggerVopCheck) {
          preparePaymentReview(values);

          return;
        }

        if (shouldTriggerVopCheck) {
          try {
            const payeeCheck = await triggerVerifyPayee({
              paymentAccountId: values.sourceAccountId,
              beneficiaryId: selectedBeneficiaryId,
              name: selectedBeneficiary.name,
              iban: selectedBeneficiary.destination.iban,
            });

            await mutations.handleIncrementalUpdates();

            if (isVopPayeeCheck(payeeCheck)) {
              if (
                !isPayeeCheckWarningRequired({
                  previousCheck: selectedBeneficiary.latestPayeeCheck,
                  newCode: payeeCheck.code,
                })
              ) {
                preparePaymentReview(values);

                return;
              }

              const paymentDetails = {
                sourceAccountId: values.sourceAccountId,
                sourceAccountName: accounts.find((a) => a.id === values.sourceAccountId)?.displayName ?? 'Bank Account',
                destinationAccountName: selectedBeneficiary.name,
                amount: values.amount,
                currency,
                reference: values.paymentReference,
              };

              updatePaymentDetails(paymentDetails);

              handleVopCheckResult(payeeCheck.code, payeeCheck.matchedName, values);

              return;
            }
          } catch (error) {
            Sentry.captureException(error);

            toast.error('Failed to verify payee');
          }
        }
      };

      const xeroCardConfig = createXeroCardConfig(externalPaymentStep);

      const closePayeeCheckModal = () => {
        setPayeeCheckModalVariantConfig(undefined);
      };

      const closePayeeConfirmationModal = () => {
        setPayeeConfirmationModalVariantConfig(undefined);
      };

      const closePayeeModal = (modalType: 'PAYEE_CHECK' | 'PAYEE_CONFIRMATION') => {
        switch (modalType) {
          case 'PAYEE_CHECK':
            closePayeeCheckModal();

            return;
          case 'PAYEE_CONFIRMATION':
            closePayeeConfirmationModal();

            return;
          default:
            return exhaustiveCheck(modalType);
        }
      };

      const onEditDetails = async (
        variantConfig: PayeeModalVariantConfig,
        modalType: 'PAYEE_CHECK' | 'PAYEE_CONFIRMATION',
      ) => {
        if (!formValuesData.externalPayment.values) {
          Sentry.captureMessage(
            'Form values data cannot be retrieved when calling onEditDetails in PayeeCheckModal in EXTERNAL_PAYMENT flow',
            {
              extra: {
                paymentAction,
                selectedBeneficiaryId,
                beneficiaries,
                formValuesData,
              },
            },
          );

          return;
        }

        if (variantConfig.variant !== VerifyPayeeCheckResponseCode.CLOSE_MATCH) {
          setExternalPaymentStep('EDIT_BENEFICIARY');

          closePayeeModal(modalType);

          return;
        }

        try {
          setIsEditingBeneficiary(true);

          await triggerUpdateBeneficiary({
            urlSuffix: `/${selectedBeneficiaryId}`,
            name: variantConfig.name,
          });

          await triggerVerifyPayee({
            paymentAccountId: formValuesData.externalPayment.values.sourceAccountId,
            beneficiaryId: selectedBeneficiaryId,
            name: variantConfig.name,
            iban: selectedBeneficiary.destination.iban,
          });

          await mutations.handleIncrementalUpdates();

          preparePaymentReview(formValuesData.externalPayment.values);

          closePayeeModal(modalType);
        } catch (error) {
          Sentry.captureException(error);

          toast.error('Failed to update beneficiary');
        } finally {
          setIsEditingBeneficiary(false);
        }
      };

      const onEditBeneficiary = async (
        values: Parameters<Parameters<typeof ExternalPaymentFlow>[0]['onEditBeneficiary']>[0],
      ) => {
        const paymentDetails = getPaymentDetails();

        if (!paymentDetails) {
          Sentry.captureMessage(
            'Payment details are not available when calling onSubmit in EditBeneficiaryFlow in ExternalPaymentFlow',
          );

          return;
        }

        const currentSourceAccount = accounts.find((a) => a.id === paymentDetails.sourceAccountId);

        if (!currentSourceAccount?.fspAdapter) {
          Sentry.captureMessage(
            'Source account fspAdapter cannot be retrieved when calling onSubmit in EditBeneficiaryFlow in ExternalPaymentFlow',
            {
              extra: {
                paymentDetails,
                sourceAccountId: paymentDetails.sourceAccountId,
                accountIds: accounts.map((a) => a.id),
              },
            },
          );

          toast.error('Source account is no longer available. Please try again.');

          return;
        }

        const { detailsFormValues, accountType } = values;

        try {
          setIsEditingBeneficiary(true);

          const beneficiaryName = getBeneficiaryName({
            legalName: detailsFormValues.legalName,
            firstName: detailsFormValues.firstName,
            lastName: detailsFormValues.lastName,
          });

          const body: UpdateBeneficiaryRequestType = {
            name: beneficiaryName,
            destination: {
              type: detailsFormValues.iban ? BeneficiaryType.IBAN : BeneficiaryType.SCAN,
              accountType,
              currency: detailsFormValues.currency,
              accountNumber: detailsFormValues.accountNumber,
              sortCode: detailsFormValues.sortCode,
              iban: detailsFormValues.iban,
            },
            country: detailsFormValues.country,
            emailAddress: detailsFormValues.email,
          };

          const updatedBeneficiary = await triggerUpdateBeneficiary({
            urlSuffix: `/${selectedBeneficiary.id}`,
            ...body,
          });

          const shouldTriggerVopCheck = isVopCheckRequired({
            currency: updatedBeneficiary.destination.currency,
            fspAdapter: currentSourceAccount.fspAdapter,
          });

          if (!shouldTriggerVopCheck) {
            await mutations.handleIncrementalUpdates();

            toast.success('Beneficiary updated successfully');

            setExternalPaymentStep('REVIEW');

            return;
          }

          if (shouldTriggerVopCheck) {
            const payeeCheckResult = await triggerVerifyPayee({
              paymentAccountId: paymentDetails.sourceAccountId,
              beneficiaryId: selectedBeneficiary.id,
              name: beneficiaryName,
              iban: detailsFormValues.iban,
            });

            await mutations.handleIncrementalUpdates();

            if (isVopPayeeCheck(payeeCheckResult)) {
              if (
                isPayeeCheckWarningRequired({
                  previousCheck: selectedBeneficiary.latestPayeeCheck,
                  newCode: payeeCheckResult.code,
                })
              ) {
                handleVopCheckResult(payeeCheckResult.code, payeeCheckResult.matchedName, {
                  amount: paymentDetails.amount,
                  sourceAccountId: paymentDetails.sourceAccountId,
                  paymentReference: paymentDetails.reference ?? '',
                });
              }
            }

            toast.success('Beneficiary updated successfully');

            return;
          }
        } catch (error) {
          Sentry.captureException(error);

          toast.error('Failed to update beneficiary');
        } finally {
          setIsEditingBeneficiary(false);
        }
      };

      return (
        <PaymentsFlowContentWrapper xeroCardConfig={xeroCardConfig}>
          <ExternalPaymentFlow
            accounts={accounts}
            paymentStep={externalPaymentStep}
            isProcessingPayment={isVerifyingPayee || isProcessingPayment}
            isEditingBeneficiary={isEditingBeneficiary}
            beneficiaryDetails={{
              id: selectedBeneficiary.id,
              name: selectedBeneficiary.name,
              currency,
              accountDetails: beneficiaryAccountDetails,
              country: selectedBeneficiary.country,
              emailAddress: selectedBeneficiary.emailAddress,
              latestPayeeCheck: isVopPayeeCheck(selectedBeneficiary.latestPayeeCheck)
                ? selectedBeneficiary.latestPayeeCheck.code
                : undefined,
            }}
            setPaymentStep={setExternalPaymentStep}
            onPrepareReview={onContinuePaymentForm}
            onSubmit={handleSubmitExternalPayment}
            onEditBeneficiary={onEditBeneficiary}
            onGoBack={onGoBack}
            onSuccessfulAuthentication={() => {
              setExternalPaymentStep('SUCCESS');

              handlePaymentSuccess();
            }}
          />

          {payeeCheckModalVariantConfig && (
            <PayeeCheckModal
              isOpen={!!payeeCheckModalVariantConfig}
              variantConfig={payeeCheckModalVariantConfig}
              isLoading={isEditingBeneficiary}
              onClose={closePayeeCheckModal}
              onEditDetails={() => onEditDetails(payeeCheckModalVariantConfig, 'PAYEE_CHECK')}
              onContinue={() => {
                closePayeeCheckModal();
                setPayeeConfirmationModalVariantConfig(payeeCheckModalVariantConfig);
              }}
            />
          )}

          {payeeConfirmationModalVariantConfig && (
            <PayeeCheckConfirmationModal
              isOpen={!!payeeConfirmationModalVariantConfig}
              variantConfig={payeeConfirmationModalVariantConfig}
              isLoading={isEditingBeneficiary}
              onClose={closePayeeConfirmationModal}
              onEditDetails={() => onEditDetails(payeeConfirmationModalVariantConfig, 'PAYEE_CONFIRMATION')}
              onContinue={() => {
                if (!formValuesData.externalPayment.values) {
                  return;
                }

                closePayeeConfirmationModal();
                preparePaymentReview(formValuesData.externalPayment.values);
              }}
            />
          )}
        </PaymentsFlowContentWrapper>
      );
    }
    case 'VIEW_ALL_BENEFICIARIES': {
      return (
        <PaymentsFlowContentWrapper>
          <BeneficiaryListFlow
            beneficiaries={beneficiaries}
            uploadInvoiceConfig={{
              isVisible: true,
              onUploadInvoice: (files) => {
                onUploadInvoice(files);

                updatePaymentsFlowEntryPoint('VIEW_ALL_BENEFICIARIES');
              },
              isUploadingInvoice,
            }}
            onGoBack={onGoBack}
            onCreateBeneficiary={() => onSetPaymentAction('CREATE_BENEFICIARY')}
            onSelectBeneficiary={onSelectBeneficiary}
          />
        </PaymentsFlowContentWrapper>
      );
    }
    case 'CREATE_BENEFICIARY': {
      const onCreateBeneficiary = async (values: CreateBeneficiaryFlowFormValues) => {
        // TODO: to be changed to isLoading. To be done in a separate PR not to bloat this one
        setIsProcessingPayment(true);

        try {
          const beneficiaryName = getBeneficiaryName({
            legalName: values.legalName,
            firstName: values.firstName,
            lastName: values.lastName,
          });

          const body: Parameters<typeof createBeneficiary>[0] = {
            name: beneficiaryName,
            destination: {
              type: values.iban ? BeneficiaryType.IBAN : BeneficiaryType.SCAN,
              accountType: values.accountType,
              currency: values.currency,
              accountNumber: values.accountNumber,
              sortCode: values.sortCode,
              iban: values.iban,
            },
            companyId: companyData.id,
            country: values.country,
            address: values.address && {
              line1: values.address?.line1,
              line2: values.address?.line2,
              postTown: values.address?.postTown,
              postCode: values.address?.postCode,
              countrySubDivision: values.address?.countrySubDivision,
            },
            emailAddress: values.email,
          };

          Sentry.addBreadcrumb({
            data: {
              body,
              message: 'Create beneficiary request body',
            },
          });

          const beneficiary = await createBeneficiary(body);

          await mutations.handleIncrementalUpdates();

          // Brings user to external payment flow as per product specs
          onSelectBeneficiary(beneficiary.id);

          toast.success('Beneficiary created successfully');
        } catch (error) {
          Sentry.captureException(error);

          toast.error('Failed to create beneficiary');
        } finally {
          setIsProcessingPayment(false);
        }
      };

      return (
        <PaymentsFlowContentWrapper>
          <CreateBeneficiaryFlow
            companyCountry={companyData.country}
            isLoading={isProcessingPayment}
            onCreateBeneficiary={onCreateBeneficiary}
            onGoBack={onGoBack}
          />
        </PaymentsFlowContentWrapper>
      );
    }
    case 'BILL_PAY': {
      const paymentsFlowEntryPoint = getPaymentsFlowEntryPoint();

      const onCancelInvoiceUpload = () => {
        if (
          paymentsFlowEntryPoint === 'BILL_PAY_UPLOAD_INVOICE' ||
          paymentsFlowEntryPoint === 'VIEW_ALL_BENEFICIARIES'
        ) {
          onSetPaymentAction(paymentsFlowEntryPoint);
        } else {
          navigate('/invoice-pay');
        }

        clearBillPayState();
        setBillPayFlowStep('FLOW');
      };

      const handleSubmitBillPayment = async (paymentDetails: PaymentDetails) => {
        clearShouldRenderReviewStep();

        const evidenceId = getUploadedEvidenceId();

        if (!evidenceId) {
          Sentry.captureMessage(
            'Evidence id cannot be retrieved when calling handleSubmitBillPayment in bill pay flow',
            {
              extra: {
                paymentAction,
                paymentDetails,
                message: 'Evidence id cannot be retrieved when calling handleSubmitBillPayment in bill pay flow',
              },
            },
          );
          toast.error('Failed to initiate payment');

          return;
        }

        const { amount, currency, sourceAccountId, reference, destinationAccountName, beneficiaryId } = paymentDetails;

        setIsProcessingPayment(true);

        try {
          const body = {
            amount,
            currency,
            destination: {
              type: PaymentDestinationType.BENEFICIARY,
              id: beneficiaryId,
              name: destinationAccountName,
            },
            sourceAccountId,
            reference,
            evidenceId,
          };

          const sourceAccount = accounts.find((a) => a.id === sourceAccountId);

          Sentry.addBreadcrumb({
            data: {
              body,
              sourceAccount,
              message: 'External payment request body',
            },
          });

          const response = await triggerPaymentBasedOnAdapter(body, sourceAccount);

          if (isAuthorisationResponse(response)) {
            // Triggers the SCA flow
            window.location.href = response.authorisationUrl;

            return;
          }

          setBillPayFlowStep('SUCCESS');

          handlePaymentSuccess();
        } catch (error) {
          Sentry.captureException(error);

          toast.error('Failed to create payment');
        } finally {
          setIsProcessingPayment(false);
        }
      };

      const isAIEnrichingEvidence =
        // User has uploaded an invoice
        !!uploadedEvidenceId &&
        // There is no error polling the evidence endpoint
        !evidenceDataError &&
        // Evidence endpoint polling has not started
        (!evidenceData ||
          // Evidence endpoint polling has started and the status is pending or processing
          [EvidenceStatus.PENDING, EvidenceStatus.PROCESSING].includes(evidenceData.status)) &&
        !shouldSkipLoadingStep;

      const onCreateBeneficiary = async (values: CreateBeneficiaryFlowFormValues) => {
        const evidenceId = getUploadedEvidenceId();

        if (!evidenceId) {
          Sentry.captureMessage('Evidence id cannot be retrieved when calling onCreateBeneficiary in bill pay flow', {
            extra: {
              evidenceId,
              message: 'Evidence id cannot be retrieved when calling onCreateBeneficiary in bill pay flow',
            },
          });

          toast.error('There was a problem creating the beneficiary');

          return;
        }

        // TODO: to be changed to isLoading. To be done in a separate PR not to bloat this one
        setIsProcessingPayment(true);

        try {
          const beneficiaryName = getBeneficiaryName({
            legalName: values.legalName,
            firstName: values.firstName,
            lastName: values.lastName,
          });

          const body: Parameters<typeof createBeneficiary>[0] = {
            name: beneficiaryName,
            destination: {
              type: values.iban ? BeneficiaryType.IBAN : BeneficiaryType.SCAN,
              accountType: values.accountType,
              currency: values.currency,
              accountNumber: values.accountNumber,
              sortCode: values.sortCode,
              iban: values.iban,
            },
            companyId: companyData.id,
            country: values.country,
            address: values.address && {
              line1: values.address?.line1,
              line2: values.address?.line2,
              postTown: values.address?.postTown,
              postCode: values.address?.postCode,
              countrySubDivision: values.address?.countrySubDivision,
            },
            emailAddress: values.email,
          };

          Sentry.addBreadcrumb({
            data: {
              body,
              message: 'Create beneficiary request body',
            },
          });

          const beneficiary = await createBeneficiary(body);

          try {
            await triggerEvidenceUpdate({
              urlSuffix: `/${evidenceId}`,
              suggestedBeneficiaryId: beneficiary.id,
            });
          } catch (error) {
            Sentry.captureMessage(
              'Failed to update evidence with suggested beneficiary id in onCreateBeneficiary in the bill pay flow',
              {
                extra: {
                  evidenceId,
                  beneficiaryId: beneficiary.id,
                  error,
                },
              },
            );
          }

          await mutations.handleIncrementalUpdates();

          toast.success('Beneficiary created successfully');

          return beneficiary;
        } catch (error) {
          Sentry.captureException(error);

          toast.error('Failed to create beneficiary');
        } finally {
          setIsProcessingPayment(false);
        }
      };

      const uploadedEvidence = evidences.find((e) => e.id === uploadedEvidenceId);

      const xeroCardConfig = createXeroCardConfig(billPayFlowStep);

      return (
        <BillPayFlow
          step={billPayFlowStep}
          // Enriched evidence
          evidence={uploadedEvidence as InvoiceEvidenceView}
          accounts={accounts}
          beneficiaries={beneficiaries}
          companyCountry={companyData.country}
          xeroCardConfig={xeroCardConfig}
          isLoading={isProcessingPayment}
          isAIEnrichingEvidence={isAIEnrichingEvidence}
          onCreateBeneficiary={onCreateBeneficiary}
          onMakePayment={handleSubmitBillPayment}
          onCancel={onCancelInvoiceUpload}
          onSuccessfulAuthentication={() => {
            setBillPayFlowStep('SUCCESS');

            handlePaymentSuccess();
          }}
          onFailedAuthentication={() => {
            updateShouldRenderReviewStep(true);
          }}
          onDone={onGoBack}
        />
      );
    }
    case 'BILL_PAY_UPLOAD_INVOICE': {
      return (
        <BillPayUploadInvoiceFlow
          onGoBack={onGoBack}
          onUploadInvoice={(files) => {
            onUploadInvoice(files);

            updatePaymentsFlowEntryPoint('BILL_PAY_UPLOAD_INVOICE');
          }}
          isUploadingInvoice={isUploadingInvoice}
        />
      );
    }
    default:
      return exhaustiveCheck(paymentAction);
  }
};

const isAuthorisationResponse = (response: PaymentResponse): response is AuthorisationResponseType => {
  return 'authorisationUrl' in response;
};
